import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useParams } from "react-router-dom";
import { DownloadOutlined, LoadingOutlined } from "@ant-design/icons";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import OverallSummary from "src/pages/OnePagerReport/V2/OverallSummary";
import CutoffTraitsTable from "src/pages/OnePagerReport/V2/CutoffTraitsTable";
import BehaviouralAnalysis from "src/pages/OnePagerReport/V2/BehaviouralAnalysis";
import FitScore from "src/pages/OnePagerReport/FitScore";
import ReportHeader from "src/pages/OnePagerReport/Header";
import TraitsGrid from "src/pages/OnePagerReport/TraitsGrid";
import useCreatedBucketsFromReport from "src/pages/OnePagerReport/V2/useCreatedBucketsFromReport";
import { ReportPdfContext } from "src/utils/contexts";
import { getTraitsMap, roundOff } from "src/utils/helperFunctions";
import { Mixpanel } from "src/services/mixpanel";
import { getTraitBand } from "src/pages/OnePagerReport/oprations";
import CandidateApi from "src/services/apis/candidate";
import PdfReportApi from "src/services/apis/pdfReport";
import { PROCTORING_TYPES } from "src/constants/globalConstants";
import CandidateDetailsSection, { normalizeCandidateFormData } from "src/pages/Positions/CandidateFormData/CandidateDetailsSection";
import "src/pages/Positions/CandidateFormData/CandidateFormData.scss";

const A4_WIDTH_PX = 794;
const A4_HEIGHT_PX = 1123;
const IGNORED_PDF_TAGS = ["AUDIO", "VIDEO", "SOURCE"];
const CONTENT_WIDTH_PX = 1150;
const CONTENT_HEIGHT_PX = 1626;
const PAGE_PADDING_PX = 92;
// Slack left at the bottom of every page to absorb cross-platform text-height
// differences, so a section is never cropped by the page's overflow:hidden.
const PAGE_HEIGHT_SAFETY_PX = 60;

const selectorsForMiniReport = [
  "#one-page-report-component .info.v2 .header",
  ".pdf-header-separator",
  ".mini-proctoring-details",
  ".candidate-details-section",
  ".overall-verdict-container",
  ".cutoff-traits-table",
  ".behavioural-analysis",
  ".fit-score-grid",
  ".fit-score",
  ".mini-report-bucket",
];

const getNumericPercentage = (value) => {
  const parsedValue = Number(value?.toString()?.split("%")?.[0]);
  return Number.isFinite(parsedValue) ? parsedValue : null;
};

/**
 * Generates a compact PDF report with only the summary view:
 * header, overall score/verdict, and bucket-level summary rows.
 *
 * Used in place of ReportDownloadComponent when the candidate's company has
 * organizationConfig.miniReport enabled (see ReportHeader).
 */
const MiniReportDownloadComponent = ({
  reportData,
  positionConfig,
  hideFitScore,
  isNonDnaReport,
  includeProctoring,
  isProctoringAvailable,
  onDownloadClick,
  onDownloadComplete,
  candidateFormData: candidateFormDataProp,
  referenceImageLink,
}) => {
  const { linkId } = useParams();
  const [loading, setLoading] = useState(false);
  const [showPortal, setShowPortal] = useState(false);
  const [pages, setPages] = useState([]);
  const [candidateFormData, setCandidateFormData] = useState(null);
  // Single proctoring image for the mini report: the reference image when the
  // assessment captured one, otherwise the earliest snapshot.
  const [proctoringImage, setProctoringImage] = useState(null);
  const hiddenContainerRef = useRef(null);
  const pageRefs = useRef([]);
  // Guards the post-render reflow so it runs at most once per download.
  const hasReflowedRef = useRef(false);

  const {
    functionalBuckets,
    communicationBuckets,
    competenciesBuckets,
    aptitudeBuckets,
    otherBuckets,
  } = useCreatedBucketsFromReport({ reportData, reportPdfView: true });

  useEffect(() => {
    if (showPortal && reportData && pages.length) {
      setTimeout(() => {
        // Estimated heights can under-report what actually renders (font
        // fallbacks differ per OS), so verify against the real pages first and
        // reflow anything that overflowed before capturing.
        if (reflowOverflowingPages()) return;
        savePdf();
      }, 1000);
    }
  }, [pages.length, reportData, showPortal]);

  useEffect(() => {
    if (includeProctoring === false) {
      generatePDF();
    }
  }, [includeProctoring]);

  useEffect(() => {
    if (candidateFormDataProp) {
      setCandidateFormData(candidateFormDataProp);
    }
  }, [candidateFormDataProp]);

  useEffect(() => {
    if (candidateFormDataProp === undefined) {
      fetchCandidateFormData();
    }
  }, [linkId, candidateFormDataProp]);

  const fetchCandidateFormData = async () => {
    try {
      const response = await CandidateApi.getCandidateFormData(linkId);
      if (response.success) {
        setCandidateFormData(normalizeCandidateFormData(response.userData));
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleDownloadIconClick = () => {
    // Parent resolves the proctoring choice and feeds it back via includeProctoring,
    // which drives generation. Without a parent handler, generate directly.
    if (onDownloadClick) {
      onDownloadClick();
    } else {
      generatePDF();
    }
  };

  const logMixpanelEvent = (event) => {
    Mixpanel.track(event, {
      "Candidate Name": reportData?.user?.name || reportData?.user?.email,
      "Position Title": reportData?.position?.name,
      Company: reportData?.position?.organizationName,
      Type: reportData?.position?.type,
      "Candidate Fit Score": roundOff(reportData?.fitScore?.score),
    });
  };

  /**
   * Split a bucket that is taller than one page.
   *
   * A bucket is: [heading block][score rows], and each score row is
   * [score][name + trait chips]. The chips are what make a bucket overflow, so
   * the break happens between chips: each chunk keeps the bucket heading, the
   * score and the bucket name, and carries the next run of chips.
   */
  const splitBucketElement = (element, maxHeight) => {
    const headingEl = element.children[0];
    const row = element.querySelector(".mini-report-score-row");
    const chips = Array.from(element.querySelectorAll(".mini-report-trait-chip"));
    if (!row || chips.length < 2) return null;

    const traitsList = chips[0].parentElement;
    const summary = traitsList?.parentElement;
    const rowsWrapper = row.parentElement;
    if (!traitsList || !summary || !rowsWrapper) return null;

    const headingHtml = headingEl?.outerHTML ?? "";
    const scoreEl = row.querySelector(".mini-report-overall-score");
    const nameEl = summary.querySelector(".bucket-info--name");

    // Height of everything except the chips themselves.
    const chrome = element.getBoundingClientRect().height -
      traitsList.getBoundingClientRect().height;
    const chipBudget = maxHeight - chrome;
    if (chipBudget <= 0) return null;

    const buildHtml = (chipsHtml) =>
      `${headingHtml}<div class="${rowsWrapper.className}">` +
        `<div class="${row.className}">` +
          `<div class="${row.firstElementChild.className}">` +
            `${scoreEl?.outerHTML ?? ""}` +
            `<div class="${summary.className}">` +
              `${nameEl?.outerHTML ?? ""}` +
              `<div class="${traitsList.className}">${chipsHtml}</div>` +
            `</div>` +
          `</div>` +
        `</div>` +
      `</div>`;

    const chunks = [];
    let currentChips = "";
    let currentChipHeight = 0;

    chips.forEach((chip) => {
      const chipHeight = chip.getBoundingClientRect().height + 8; // + list gap

      if (currentChips && currentChipHeight + chipHeight > chipBudget) {
        chunks.push({ html: buildHtml(currentChips), height: chrome + currentChipHeight });
        currentChips = "";
        currentChipHeight = 0;
      }

      currentChips += chip.outerHTML;
      currentChipHeight += chipHeight;
    });

    if (currentChips) {
      chunks.push({ html: buildHtml(currentChips), height: chrome + currentChipHeight });
    }

    return chunks.length > 1 ? chunks : null;
  };

  const calculatePagesForPdf = (elementsForHeightCalculation) => {
    // Safety margin: font fallbacks differ per OS (MessinaSans fails CORS), so a
    // measured height can under-report what actually renders. Without slack the
    // page's overflow:hidden crops the bottom of the last section.
    const maxPageHeight = CONTENT_HEIGHT_PX - PAGE_PADDING_PX * 3 - PAGE_HEIGHT_SAFETY_PX;
    let currentPage = [];
    let remainingHeight = maxPageHeight;
    const newPages = [];

    const startNewPage = () => {
      if (currentPage.length) newPages.push(currentPage);
      currentPage = [];
      remainingHeight = maxPageHeight;
    };

    const place = (entry) => {
      if (currentPage.length && remainingHeight < entry.height) startNewPage();
      currentPage.push(entry);
      remainingHeight -= entry.height;
    };

    elementsForHeightCalculation.forEach(({ element, height, useOuterHTML = false, startWithNewPage = false }) => {
      if (!element) return;

      if (startWithNewPage) startNewPage();

      if (height > maxPageHeight) {
        // Continue the bucket on the next page, repeating its name, instead of
        // letting the page's overflow:hidden crop the remaining traits.
        const chunks = splitBucketElement(element, maxPageHeight);

        if (chunks) {
          chunks.forEach(({ html, height: chunkHeight }, i) => {
            if (i > 0) startNewPage();
            place({ element, height: Math.min(chunkHeight, maxPageHeight), useOuterHTML, html });
          });
          return;
        }

        // Nothing splittable: scale it down rather than cropping.
        startNewPage();
        newPages.push([
          { element, height: maxPageHeight, useOuterHTML, scale: maxPageHeight / height },
        ]);
        remainingHeight = maxPageHeight;
        return;
      }

      place({ element, height, useOuterHTML });
    });

    if (currentPage.length) newPages.push(currentPage);
    setPages(newPages);
  };

  const collectMiniReportElements = () => {
    const mainSection = hiddenContainerRef.current?.querySelector(".main-section");
    const elementsForCapture = [];

    selectorsForMiniReport.forEach((selector) => {
      const selectorElements = mainSection?.querySelectorAll(selector) ?? [];
      selectorElements.forEach((element, index) => {
        elementsForCapture.push({
          element,
          height: element.getBoundingClientRect().height,
          useOuterHTML: selector === "#one-page-report-component .info.v2 .header",
          // Buckets begin on a fresh page, after the candidate/summary page.
          // Only the first one breaks; the rest flow on from there.
          startWithNewPage: selector === ".mini-report-bucket" && index === 0,
        });
      });
    });

    return elementsForCapture;
  };

  /**
   * Inline a remote image as a data URI. html2canvas runs without useCORS here,
   * so a cross-origin <img> would taint the canvas and capture blank; drawing it
   * to a canvas first also guarantees it is fully loaded before capture.
   */
  const toDataUri = (imageUrl) =>
    new Promise((resolve) => {
      const image = new Image();
      image.crossOrigin = "anonymous";

      image.onload = () => {
        try {
          const canvas = document.createElement("canvas");
          canvas.width = image.width;
          canvas.height = image.height;
          canvas.getContext("2d").drawImage(image, 0, 0);
          resolve(canvas.toDataURL("image/jpeg", 0.8));
        } catch (error) {
          console.error("Mini report proctoring image inlining failed:", error);
          resolve(null);
        }
      };
      image.onerror = () => resolve(null);
      setTimeout(() => resolve(null), 5000);

      image.src = imageUrl;
    });

  /**
   * Resolve the one proctoring image the mini report shows: the reference image
   * when the assessment captured one, otherwise the earliest snapshot. Resolved
   * before the portal renders so html2canvas captures a loaded <img>.
   */
  /**
   * The reference image is returned by the per-session snapshot endpoint, not by
   * the link-level one, so look it up through the candidate's video sessions.
   * referenceImageLink from the parent is only set once the proctoring modal has
   * fetched a session, so it is usually null at download time.
   */
  const fetchReferenceImageLink = async () => {
    try {
      const sessionsResponse = await PdfReportApi.getVideoSessions(linkId, PROCTORING_TYPES.VIDEO);
      const sessions = sessionsResponse?.success ? sessionsResponse.data ?? [] : [];

      for (const session of sessions) {
        const snapshotsResponse = await PdfReportApi.getSessionSnapshots(session.id);
        if (snapshotsResponse?.referenceImageLink) return snapshotsResponse.referenceImageLink;
      }
    } catch (error) {
      console.error("Error fetching mini report reference image:", error);
    }
    return null;
  };

  const resolveProctoringImage = async () => {
    if (!isProctoringAvailable) return null;

    try {
      // Fetch directly rather than via the parent's fetchSnapshots: that helper
      // reads allSnapshots, which only ProctoringModal populates, so it is empty
      // unless the user opened the modal before downloading.
      const response = await PdfReportApi.fetchSnapshots(linkId);
      const snapshots = response?.success ? response.data ?? [] : [];

      const resolvedReferenceImage = referenceImageLink || (await fetchReferenceImageLink());
      if (resolvedReferenceImage) {
        const link = await toDataUri(resolvedReferenceImage);
        if (link) return { link, label: "Reference Image" };
      }

      const [firstSnapshot] = [...snapshots].sort(
        (a, b) => new Date(a?.createdAt) - new Date(b?.createdAt)
      );
      const firstSnapshotLink = firstSnapshot?.media?.link ?? firstSnapshot?.snapshotLink;
      if (!firstSnapshotLink) return null;

      // Labelled as the reference image either way: the first snapshot stands in
      // for it when the assessment did not capture a dedicated reference image.
      const link = await toDataUri(firstSnapshotLink);
      return link ? { link, label: "Reference Image" } : null;
    } catch (error) {
      console.error("Error resolving mini report proctoring image:", error);
      return null;
    }
  };

  const generatePDF = async () => {
    setLoading(true);
    setPages([]);
    pageRefs.current = [];
    hasReflowedRef.current = false;

    setProctoringImage(await resolveProctoringImage());
    setShowPortal(true);

    await new Promise((resolve) => setTimeout(resolve, 1500));

    // Measure only once fonts have settled. A fallback font swapping in after
    // measurement changes text height, which silently breaks the page maths --
    // and the fallback differs between macOS and Windows.
    try {
      await document.fonts?.ready;
    } catch (error) {
      /* fonts API unavailable -- the timeout above is the fallback */
    }

    try {
      const elements = collectMiniReportElements();
      calculatePagesForPdf(elements);
      if (!elements.length) {
        setShowPortal(false);
        setLoading(false);
        onDownloadComplete?.();
      }
    } catch (error) {
      console.error("Error during mini PDF generation:", error);
      setShowPortal(false);
      setLoading(false);
      onDownloadComplete?.();
    }
  };

  /**
   * Measure the rendered pages and push overflowing sections onto the next page.
   *
   * calculatePagesForPdf works from heights measured in the hidden portal, which
   * can be optimistic once a different fallback font renders. Here the pages
   * exist for real, so scrollHeight tells us exactly what spilled past the page
   * box -- whatever overflowed moves down a page. Returns true when it rebuilt
   * the layout (a re-render follows, and this effect runs again).
   */
  const reflowOverflowingPages = () => {
    if (hasReflowedRef.current) return false;

    const usableHeight = CONTENT_HEIGHT_PX - PAGE_PADDING_PX * 2;
    let changed = false;

    // Measure every page first: inserting pages while iterating would shift the
    // indices out of step with pageRefs.
    const overflowByPage = pageRefs.current.map((pageEl, pageIndex) => {
      if (!pageEl) return -1;

      const sections = Array.from(pageEl.children).filter(
        (child) => child.tagName !== "STYLE"
      );
      const pageEntries = pages[pageIndex];
      if (!pageEntries || sections.length < 2) return -1;

      let used = 0;
      let overflowAt = -1;

      sections.forEach((section, i) => {
        used += section.getBoundingClientRect().height;
        if (overflowAt === -1 && used > usableHeight) overflowAt = i;
      });

      // Never move the only section on a page: it would loop forever.
      return overflowAt > 0 && overflowAt < pageEntries.length ? overflowAt : -1;
    });

    const nextPages = [];
    pages.forEach((pageElements, pageIndex) => {
      const overflowAt = overflowByPage[pageIndex] ?? -1;

      if (overflowAt === -1) {
        nextPages.push([...pageElements]);
        return;
      }

      nextPages.push(pageElements.slice(0, overflowAt));
      nextPages.push(pageElements.slice(overflowAt));
      changed = true;
    });

    if (!changed) return false;

    hasReflowedRef.current = true;
    pageRefs.current = [];
    setPages(nextPages);
    return true;
  };

  const savePdf = async () => {
    try {
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "px",
        format: [A4_WIDTH_PX, A4_HEIGHT_PX],
        compress: true,
      });

      for (let i = 0; i < pageRefs.current.length; i++) {
        if (!pageRefs.current[i]) continue;
        const canvas = await html2canvas(pageRefs.current[i], {
          scale: 1.4,
          // <source> inside <audio>/<video> has no computed style and crashes html2canvas.
          ignoreElements: (el) => IGNORED_PDF_TAGS.includes(el.tagName),
        });
        const imgData = canvas.toDataURL("image/jpeg", 0.5);

        if (i > 0) pdf.addPage();
        pdf.addImage(imgData, "JPEG", 0, 0, A4_WIDTH_PX, A4_HEIGHT_PX);
      }

      const fileNameParts = [reportData?.user?.name, reportData?.position?.name].filter(Boolean);
      pdf.save(`${fileNameParts.join(" - ") || "Report"} Mini Report.pdf`);
    } catch (error) {
      console.error("Error while saving mini PDF:", error);
    } finally {
      setShowPortal(false);
      setLoading(false);
      onDownloadComplete?.();
    }
  };

  const renderMiniBucket = (bucket) => {
    const { score: { score, lowerBound, upperBound, median } = {}, traitsMeta } = bucket ?? {};
    const color = bucket
      ? getTraitBand({
        score,
        lb: lowerBound,
        ub: upperBound,
        median,
      })?.color
      : "#000";

    return (
      <div className="overall-score mini-report-score-row" key={bucket.id}>
        <div className="bucket-info mini-report-bucket-info">
          <h4 className="bucket-info--score mini-report-overall-score" style={{ color }}>
            {roundOff(score)}
          </h4>
          <div className="mini-report-trait-summary">
            <h4 className="bucket-info--name">{bucket.name}</h4>
            {!!traitsMeta && (
              <div className="traits-list mini-report-traits-list">
                {Object.values(traitsMeta).map((trait) => {
                  const { id, score, displayName, upperBound, lowerBound, median } = trait;
                  const traitBand = getTraitBand({
                    score,
                    lb: lowerBound?.score,
                    ub: upperBound?.score,
                    median,
                  });

                  return (
                    <h2
                      key={id}
                      className="trait-box small v2 v2--single-row-bucket mini-report-trait-chip"
                    >
                      <span
                        className="mini-report-trait-score"
                        style={{ color: traitBand.textColor }}
                      >
                        {roundOff(score)}
                      </span>
                      <span className="mini-report-trait-separator">-</span>
                      <span className="mini-report-trait-name">{displayName}</span>
                    </h2>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderBucketSummary = ({ name, description, buckets }) => {
    if (!buckets?.length) return null;

    return (
      <div className="single-bucket mini-report-bucket">
        <div>
          <h4 className="single-bucket--name">{name}</h4>
          {description && <p className="single-bucket--description">{description}</p>}
        </div>
        <div>{buckets.map((bucket) => renderMiniBucket(bucket))}</div>
      </div>
    );
  };

  const renderProctoringDetails = () => {
    const trustScore = reportData?.trustScore;
    const tabSwitchedCount = reportData?.tabSwitched ?? 0;
    const hideTrustScoreDescription = positionConfig?.skipAiProcOnCutoffFail && reportData?.cutOffCleared === false;
    const trustScoreNum = getNumericPercentage(trustScore?.trustScore);
    const hasTrustScoreValue = trustScoreNum !== null;
    const hasTrustScoreRemark = !!trustScore?.remark;

    if (!hasTrustScoreRemark && !isProctoringAvailable && !proctoringImage) return null;

    const bandStyle = hasTrustScoreValue
      ? getTraitBand({
        median: 50,
        lb: 25,
        ub: 75,
        score: roundOff(trustScoreNum),
      })
      : null;

    return (
      <div className="single-bucket mini-proctoring-details">
        <div>
          <h4 className="single-bucket--name">PROCTORING DETAILS</h4>
        </div>
        <div className="mini-proctoring-content">
          {(hasTrustScoreValue || hasTrustScoreRemark) && (
            <div className="mini-proctoring-card mini-proctoring-trust-card">
              <div className="mini-proctoring-row">
                <h4 className="mini-proctoring-title">Trust Score</h4>
                {hasTrustScoreValue && (
                  <p
                    className="mini-proctoring-score"
                    style={{ color: bandStyle?.textColor }}
                  >
                    {roundOff(trustScoreNum)}%
                  </p>
                )}
              </div>
              {!hideTrustScoreDescription && hasTrustScoreRemark && (
                <p className="mini-proctoring-remark">{trustScore.remark}</p>
              )}
            </div>
          )}
          {isProctoringAvailable && (
            <div className="mini-proctoring-card mini-proctoring-tabs-card">
              <div className="mini-proctoring-row">
                <h4 className="mini-proctoring-title">Tabs Switched</h4>
                <p className="mini-proctoring-score">{tabSwitchedCount}</p>
              </div>
            </div>
          )}
          {proctoringImage && (
            <div className="mini-proctoring-image-block">
              <img
                className="mini-proctoring-image"
                src={proctoringImage.link}
                alt={proctoringImage.label}
              />
              <p className="mini-proctoring-image-label">{proctoringImage.label}</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <ReportPdfContext.Provider value={{ reportPdfView: true }}>
      <div>
        {!loading ? (
          <DownloadOutlined
            onClick={handleDownloadIconClick}
            className="copy-url"
            title="Download Mini Report"
          />
        ) : (
          <LoadingOutlined
            className="copy-url"
            title="Generating Mini Report"
            disabled={true}
          />
        )}

        {showPortal &&
          ReactDOM.createPortal(
            <div
              ref={hiddenContainerRef}
              style={{
                display: "block",
                position: "absolute",
                top: "-9999px",
                left: "-9999px",
                backgroundColor: "transparent",
              }}
            >
              <section className="main-section">
                <div id="one-page-report-component" className="v2">
                  <div className="info v2">
                    <ReportHeader
                      hideCta
                      reportData={reportData}
                      positionConfig={positionConfig}
                      userVideoSessionsList={[]}
                      isNonDnaReport={isNonDnaReport}
                      logMixpanelEvent={logMixpanelEvent}
                      hideVerdictPills={reportData?.cutOffCleared != null}
                    />
                    <div className="pdf-header-separator" />
                    <div className="content-wrapper">
                      <div className="max-width-left">
                        {renderProctoringDetails()}
                        <CandidateDetailsSection
                          formData={candidateFormData}
                          hideWhenEmpty
                          reportView
                        />
                        {reportData?.cutOffCleared != null ? (
                          <>
                            <OverallSummary reportData={reportData} positionConfig={positionConfig} />
                            <CutoffTraitsTable reportData={reportData} />
                            {reportData?.fitScore && reportData?.fitScore?.cutOff == null && (
                              <div className="fit-score-grid">
                                <TraitsGrid
                                  showBucketWise
                                  hideLegends
                                  title="Overall Score"
                                  description="Overall score based on performance across all assessed skills."
                                  traitsMap={getTraitsMap([{
                                    traitsMeta: {
                                      fitScore: {
                                        id: "overallScore",
                                        name: "Overall Score",
                                        displayName: "Overall Score",
                                        description: "Overall score",
                                        upperBound: { score: reportData.fitScore.data?.upperBound },
                                        lowerBound: { score: reportData.fitScore.data?.lowerBound },
                                        score: reportData.fitScore.score,
                                        median: reportData.fitScore.data?.median,
                                      },
                                    },
                                  }])}
                                />
                              </div>
                            )}
                          </>
                        ) : !hideFitScore ? (
                          <FitScore
                            {...reportData?.fitScore}
                            dnaScore={null}
                            showLegends={isNonDnaReport}
                            fitScoreMultiplier={positionConfig?.fitScoreMultiplier ?? 1}
                          />
                        ) : null}

                        <BehaviouralAnalysis behaviouralAnalysis={reportData?.behaviouralAnalysis} />
                        {renderBucketSummary(functionalBuckets)}
                        {renderBucketSummary(communicationBuckets)}
                        {renderBucketSummary(competenciesBuckets)}
                        {renderBucketSummary(aptitudeBuckets)}
                        {otherBuckets?.map((bucket) => (
                          <React.Fragment key={bucket.id}>
                            {renderBucketSummary({
                              name: bucket.name,
                              description: bucket.description,
                              buckets: [bucket],
                            })}
                          </React.Fragment>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            </div>,
            document.body
          )}

        <div style={{ position: "absolute", left: "-9999px", top: "-9999px" }}>
          {pages.map((pageElements, index) => (
            <A4PageWrapper key={index} ref={(el) => (pageRefs.current[index] = el)}>
              {pageElements.map(({ element, useOuterHTML, scale, html }, idx) => (
                <div
                  key={idx}
                  className={element.className}
                  style={scale ? { transform: `scale(${scale})`, transformOrigin: "top left" } : undefined}
                  dangerouslySetInnerHTML={{
                    // `html` is set for one chunk of a bucket split across pages.
                    __html: html ?? (useOuterHTML ? element.outerHTML : element.innerHTML),
                  }}
                />
              ))}
            </A4PageWrapper>
          ))}
        </div>
      </div>
    </ReportPdfContext.Provider>
  );
};

export default MiniReportDownloadComponent;

const A4PageWrapper = React.forwardRef(({ children }, ref) => (
  <div
    ref={ref}
    className="report-download"
    style={{
      width: `${CONTENT_WIDTH_PX}px`,
      height: `${CONTENT_HEIGHT_PX}px`,
      padding: `${PAGE_PADDING_PX}px`,
      background: "white",
      overflow: "hidden",
      marginBottom: "20px",
      boxShadow: "0 0 5px rgba(0,0,0,0.1)",
      boxSizing: "border-box",
    }}
  >
    <style>
      {`
        .report-download .header {
          border-bottom: none !important;
          padding: 0 !important;
        }
        .report-download .pdf-header-separator {
          border-bottom: 1px solid #babacd;
          width: 100%;
          margin-bottom: 28px;
        }
        .report-download .behavioural-analysis {
          border-color: #e4e4e4;
        }
        .report-download .mini-report-score-row {
          max-width: 100%;
          overflow: hidden;
        }
        .report-download .mini-report-bucket-info {
          align-items: center;
          gap: 28px;
          max-width: 100%;
        }
        .report-download .mini-report-overall-score {
          min-width: 84px;
          flex: 0 0 auto;
          margin-right: 0 !important;
        }
        .report-download .mini-report-trait-summary {
          display: flex;
          align-items: center;
          gap: 18px;
          min-width: 0;
          flex: 1 1 auto;
        }
        .report-download .mini-report-trait-summary .bucket-info--name {
          margin: 0;
          white-space: nowrap;
        }
        .report-download .mini-report-traits-list {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-top: 0 !important;
          min-width: 0;
          flex: 1 1 auto;
        }
        .report-download .mini-report-trait-chip {
          align-items: baseline;
          display: inline-flex;
          gap: 5px;
          letter-spacing: 0 !important;
          margin: 0 !important;
          flex: 0 1 auto;
          max-width: 100%;
          min-width: 0;
          white-space: normal !important;
          overflow-wrap: normal;
        }
        .report-download .mini-report-trait-score {
          flex: 0 0 auto;
          white-space: nowrap;
        }
        .report-download .mini-report-trait-name {
          min-width: 0;
          overflow-wrap: break-word;
        }
        .report-download .mini-report-trait-separator {
          flex: 0 0 auto;
          margin-left: 4px;
          margin-right: 4px;
        }
        .report-download .mini-proctoring-details {
          display: grid;
          grid-template-columns: 150px minmax(0, 1fr);
          column-gap: 24px;
          align-items: start;
          break-inside: avoid;
          padding-bottom: 30px;
        }
        .report-download .mini-proctoring-content {
          display: grid;
          grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr) minmax(0, 1fr);
          gap: 20px;
          align-items: stretch;
          width: 100%;
        }
        .report-download .mini-proctoring-card {
          border: 1px solid #e5e5ef;
          border-radius: 8px;
          padding: 18px 20px;
          margin-top: 0;
          box-sizing: border-box;
        }
        .report-download .mini-proctoring-row {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 14px;
        }
        .report-download .mini-proctoring-title {
          color: #242238;
          font-size: 20px;
          font-weight: 700;
          margin: 0;
        }
        .report-download .mini-proctoring-score {
          color: #242238;
          font-size: 20px;
          font-weight: 700;
          margin: 0;
        }
        .report-download .mini-proctoring-remark {
          color: #6f6d7d;
          font-size: 16px;
          line-height: 24px;
          margin: 12px 0 0;
        }
        .report-download .mini-proctoring-image-block {
          grid-column: 3;
          display: flex;
          flex-direction: column;
          margin-top: 0;
        }
        .report-download .mini-proctoring-image {
          width: 100%;
          flex: 1 1 auto;
          min-height: 0;
          max-height: 170px;
          object-fit: cover;
          border: 1px solid #e5e5ef;
          border-radius: 8px;
          display: block;
          box-sizing: border-box;
        }
        .report-download .mini-proctoring-image-label {
          color: #6f6d7d;
          font-size: 14px;
          margin: 8px 0 0;
          text-align: center;
        }
      `}
    </style>
    {children}
  </div>
));
